import { apiSlice } from "../api/apiSlice";
import getPaginationInfo from "../../utils/getPaginationInfo";
import getPaginationFromBody from "../../utils/getPaginationFromBody";
import paramConfig from "../../config/paramConfig";

/**
 * Slim list-row shape returned by `GET /features`. Anything not listed here
 * (linked_conclusion_ids, etc.) requires the per-id detail endpoint.
 * @typedef {Object} FeatureSummary
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {string[]} spectrum_ids
 * @property {string} date_created
 * @property {string} date_modified
 */

/**
 * Full feature shape returned by `GET /features/:id`. Includes everything in
 * FeatureSummary plus relational fields (linked conclusions, etc.).
 * @typedef {FeatureSummary & Record<string, unknown>} Feature
 */

export const featuresSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // List endpoint — returns FeatureSummary[]. Do NOT pre-seed the per-id
    // detail cache from these entries: the slim shape would mask a needed
    // detail fetch and detail pages would render with missing fields.
    getFeatures: builder.query({
      query: ({ page = 1, pageSize = 600 }) =>
        `/features?${paramConfig.PAGE_NUMBER}=${page}&${paramConfig.PAGE_SIZE}=${pageSize}`,
      transformResponse: (response, meta) => {
        const pagination = getPaginationInfo(meta);
        return { response, pagination };
      },
      providesTags: (result, error, arg) => [
        { type: "Feature" },
        { type: "Feature", id: arg?.page },
        { type: "Feature", id: arg?.pageSize },
      ],
    }),
    // Detail endpoint — returns the full Feature.
    getFeature: builder.query({
      query: ({ id }) => `/features/${id}`,
      providesTags: (result, error, arg) => [{ type: "Feature", id: arg?.id }],
    }),
    getFeatureSpectra: builder.query({
      query: ({ id, label, page = 1, pageSize = 24 }) =>
        `/features/${id}/examples?label=${label}&${paramConfig.PAGE_NUMBER}=${page}&${paramConfig.PAGE_SIZE}=${pageSize}`,
      transformResponse: (response, meta) => {
        const pagination = getPaginationInfo(meta);
        return { response, pagination };
      },
      providesTags: (result, error, arg) => [
        { type: "FeatureSpectra", id: `${arg?.id}-${arg?.label}` },
      ],
    }),
    patchFeatureExample: builder.mutation({
      query: ({ params, body }) => ({
        url: `/features/${params?.featureId}/examples/${params?.spectrumId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "FeatureSpectra", id: `${arg?.params?.featureId}-positive` },
        { type: "FeatureSpectra", id: `${arg?.params?.featureId}-negative` },
        { type: "FeatureSpectra", id: `${arg?.params?.featureId}-unassigned` },
        { type: "FeaturePredictions", id: arg?.params?.featureId },
        // Re-labelling an example changes the training set, so both the
        // predictions and the model's live_evaluation go stale.
        { type: "FeatureModel", id: arg?.params?.featureId },
        { type: "Feature", id: arg?.params?.featureId },
        { type: "SpectrumFeatures", id: `${arg?.params?.spectrumId}-positive` },
        { type: "SpectrumFeatures", id: `${arg?.params?.spectrumId}-negative` },
        {
          type: "SpectrumFeatures",
          id: `${arg?.params?.spectrumId}-unassigned`,
        },
      ],
    }),
    getFeaturePredictions: builder.query({
      query: ({ id, bucket, sort = "probability", page = 1, pageSize = 24 }) =>
        `/features/${id}/predictions?bucket=${bucket}&sort=${sort}&${paramConfig.PAGE_NUMBER}=${page}&${paramConfig.PAGE_SIZE}=${pageSize}`,
      transformResponse: (response) => {
        const pagination = getPaginationFromBody(response);
        return {
          items: response?.items || [],
          counts: response?.counts || {},
          model: response?.model || null,
          pagination,
        };
      },
      providesTags: (result, error, arg) => [
        { type: "FeaturePredictions", id: arg?.id },
        { type: "FeaturePredictions", id: `${arg?.id}-${arg?.bucket}` },
      ],
    }),
    getFeatureModel: builder.query({
      query: ({ id }) => `/features/${id}/model`,
      providesTags: (result, error, arg) => [
        { type: "FeatureModel", id: arg?.id },
      ],
    }),
    trainFeatureModel: builder.mutation({
      query: ({ params }) => ({
        url: `/features/${params?.featureId}/model/train`,
        method: "POST",
      }),
    }),
    rollbackFeatureModel: builder.mutation({
      query: ({ params }) => ({
        url: `/features/${params?.featureId}/model/rollback`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "FeatureModel", id: arg?.params?.featureId },
      ],
    }),
    postFeature: builder.mutation({
      query: ({ body }) => {
        return {
          url: `/features`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: [{ type: "Feature" }],
    }),
    deleteFeature: builder.mutation({
      query: ({ params }) => {
        return {
          url: `/features/${params?.id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [{ type: "Feature" }],
    }),
    patchFeature: builder.mutation({
      query: ({ params, body }) => {
        return {
          url: `/features/${params?.featureId}`,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Feature" },
        { type: "Feature", id: arg?.params?.featureId },
      ],
    }),
    postConclusionForFeature: builder.mutation({
      query: ({ params, body }) => {
        return {
          url: `/features/${params?.featureId}/conclusions`,
          method: "POST",
          body,
        };
      },
      async onQueryStarted({ body }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            apiSlice.util.invalidateTags([
              { type: "Conclusion" },
              { type: "Conclusion", id: body?.conclusion_id },
            ])
          );
        } catch (err) {
          console.error(err);
        }
      },
    }),
    deleteConclusionForFeature: builder.mutation({
      query: ({ params }) => {
        return {
          url: `/features/${params?.featureId}/conclusions/${params?.conclusionId}`,
          method: "DELETE",
        };
      },
      async onQueryStarted({ params }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            apiSlice.util.invalidateTags([
              { type: "Conclusion", id: params?.conclusionId },
            ])
          );
        } catch (err) {
          console.error(err);
        }
      },
    }),
  }),
});

export const {
  useGetFeaturesQuery,
  useGetFeatureQuery,
  useGetFeatureSpectraQuery,
  usePatchFeatureExampleMutation,
  useGetFeaturePredictionsQuery,
  useGetFeatureModelQuery,
  useTrainFeatureModelMutation,
  useRollbackFeatureModelMutation,
  usePostFeatureMutation,
  useDeleteFeatureMutation,
  usePatchFeatureMutation,
  usePostConclusionForFeatureMutation,
  useDeleteConclusionForFeatureMutation,
} = featuresSlice;
